import { Link } from "@tanstack/react-router";
import { trackEvent } from "@/lib/analytics-events";
import { smsHref } from "@/components/TextUs";

const SEASON_YEAR = 2026;

// Update availability here every Monday. Badge is based on sprinters + suvs left:
// 3+ = "Available" (gold), 1–2 = "Almost gone" (red), 0 = "Sold out" (grey, crossed out).
const BIG_DATES = [
  { date: "Sat, Nov 14", event: "UGA vs. Missouri", location: "Athens", sprinters: 2, suvs: 2 },
  { date: "Sat, Nov 28", event: "UGA vs. Georgia Tech", location: "Athens", sprinters: 2, suvs: 2 },
  { date: "Sat, Dec 5", event: "SEC Championship", location: "Mercedes-Benz Stadium", sprinters: 2, suvs: 2 },
] as { date: string; event: string; location: string; sprinters: number; suvs: number; tag?: string }[];

const MONTHS: Record<string, string> = { Jan: "01", Feb: "02", Mar: "03", Apr: "04", May: "05", Jun: "06", Jul: "07", Aug: "08", Sep: "09", Oct: "10", Nov: "11", Dec: "12" };
function isoDate(date: string) {
  const m = date.match(/([A-Z][a-z]{2})\s+(\d{1,2})/);
  if (!m || !MONTHS[m[1]]) return date;
  return `${SEASON_YEAR}-${MONTHS[m[1]]}-${m[2].padStart(2, "0")}`;
}
function rowMessage(d: { date: string; event: string; location: string }, soldOut: boolean) {
  const venue = d.location.split(",")[0].trim();
  return soldOut
    ? `Hi! I'd like to join the waitlist for ${d.date} – ${d.event}. Group size: __ Pickup address: __`
    : `Hi! I'd like to check availability for ${d.date} – ${d.event} (${venue}). Group size: __ Pickup address: __`;
}

function gameDayLink(d: { event: string; location: string }) {
  if (d.location === "Athens") return { to: "/uga-game-day-transportation" as const, label: "UGA game day transportation" };
  if (d.event === "Georgia vs. Florida") return { to: "/georgia-vs-florida-game-transportation" as const, label: "Georgia vs. Florida transportation" };
  if (d.location.startsWith("Mercedes-Benz Stadium")) return { to: "/mercedes-benz-stadium-transportation" as const, label: "Mercedes-Benz Stadium transportation" };
  return null;
}

export default function BigDates({
  athensOnly = false,
  events,
  heading = "Atlanta's Biggest Dates Book First",
  showGameDayLink = true,
  note,
}: { athensOnly?: boolean; events?: string[]; heading?: string; showGameDayLink?: boolean; note?: string } = {}) {
  const rows = events
    ? BIG_DATES.filter((d) => events.includes(d.event))
    : athensOnly ? BIG_DATES.filter((d) => d.location === "Athens") : BIG_DATES;
  if (rows.length === 0) return null;
  return (
    <section
      id="big-dates"
      data-track-section="big-dates"
      data-track-location="big_dates"
      className="bg-foreground text-background py-14 sm:py-20"
    >
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="text-center mb-8 sm:mb-10">
          <h2 className="font-serif text-2xl sm:text-4xl mb-3">{heading}</h2>
          <p className="text-background/70 text-sm sm:text-base">
            Custom Sprinters. When a date is gone, it's gone.
          </p>
        </div>

        <ul className="space-y-3">
          {rows.map((d) => {
            const left = d.sprinters + d.suvs;
            const soldOut = left === 0;
            return (
              <li
                key={d.date + d.event}
                className={`rounded-md border border-background/10 bg-background/5 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5 ${soldOut ? "opacity-60" : ""}`}
              >
                <div className="sm:w-28 shrink-0 text-gold font-medium text-sm uppercase tracking-wide">
                  {d.date}
                </div>
                <div className={`flex-1 min-w-0 ${soldOut ? "line-through" : ""}`}>
                  <div className="font-serif text-lg">{d.event}</div>
                  <div className="text-background/60 text-sm">{d.location}</div>
                  {d.tag && (
                    <span className="inline-block mt-1.5 text-[11px] uppercase tracking-wide rounded-sm px-2 py-0.5 bg-gold/15 text-gold no-underline">
                      {d.tag}
                    </span>
                  )}
                  {showGameDayLink && gameDayLink(d) && (
                    <Link to={gameDayLink(d)!.to} className="block mt-1 text-xs text-gold underline underline-offset-2">
                      {gameDayLink(d)!.label}
                    </Link>
                  )}
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="text-right">
                    <div className="text-background/60 text-xs mt-1">Check your date for current availability.</div>
                  </div>
                  {(() => {
                    const availability = soldOut ? "sold_out" : left <= 2 ? "almost_gone" : "available";
                    const event_date = isoDate(d.date);
                    const label = soldOut ? "Join the Waitlist" : "Check My Date";
                    const track = () => {
                      trackEvent("check_date_click", { link_location: "big_dates", event_date, event_name: d.event, availability });
                      trackEvent("sms_click", { link_location: "big_dates", event_date, page_path: window.location.pathname });
                    };
                    const cls = "btn-primary-hero rounded-sm text-center !px-4 !py-2 text-sm no-underline";
                    return (
                      <a href={smsHref(rowMessage(d, soldOut))} data-track-manual onClick={track} className={cls}>
                        {label}
                      </a>
                    );
                  })()}
                </div>
              </li>
            );
          })}
        </ul>

        {note && <p className="text-center text-background/80 text-sm mt-6">{note}</p>}
        <p className="text-center text-background/50 text-xs mt-6">Updated every Monday.</p>
      </div>
    </section>
  );
}
